<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import markdownToHtmlConverter from './MarkdownToHtmlConverter.js';

const props = defineProps({
  procedure: {
    type: Object,
    required: true,
  },
  procedureIndex: {
    type: Number,
    required: true,
  },
  catalogTemplate: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['moveStep', 'toggleDisabled']);

const store = useStore();

const catalogStepsByKey = computed(() => {
  const steps = props.catalogTemplate?.steps || [];
  return Object.fromEntries(steps.map(step => [step.key, step]));
});

function catalogStep(step) {
  return catalogStepsByKey.value[step.key] || {};
}

function stepTitle(step, stepIndex) {
  return catalogStep(step).title || step.key || stepIndex + 1;
}

function instructionSource(step) {
  return step.instruction || catalogStep(step).instruction || '';
}

function instructionHtml(step) {
  return markdownToHtmlConverter.convertToHtml(instructionSource(step), store);
}

function move(stepIndex, direction) {
  emit('moveStep', {
    procedureIndex: props.procedureIndex,
    from: stepIndex,
    to: stepIndex + direction,
  });
}

function toggleDisabled(stepIndex) {
  emit('toggleDisabled', {
    procedureIndex: props.procedureIndex,
    stepIndex,
  });
}
</script>

<template>
  <div class="steps-container">
    <div
      v-for="(step, stepIndex) in procedure.steps"
      :key="step.key || stepIndex"
      class="step-container mb-3 mt-4 bg-white dark:bg-n-slate-2 rounded-lg"
      :class="{ 'opacity-40': step.disabled }"
    >
      <div class="flex justify-between items-center mb-3">
        <div class="flex items-center gap-3">
          <h5 class="text-sm font-bold text-n-slate-11 dark:text-n-slate-10">
            {{
              $t('ASSISTANT_MGMT.PROCEDURES.STEP_NUMBER', {
                number: stepIndex + 1,
              })
            }}
            <span class="font-medium text-n-slate-10">
              · {{ stepTitle(step, stepIndex) }}
            </span>
          </h5>
          <span
            v-if="step.type === 'required'"
            class="inline-block px-2 py-1 text-xs rounded bg-slate-500 dark:bg-slate-800 text-slate-50 dark:text-slate-100 font-bold"
          >
            {{ $t('ASSISTANT_MGMT.PROCEDURES.STEP_TYPES.REQUIRED') }}
          </span>
          <span
            v-if="step.disabled"
            class="inline-block px-2 py-1 text-xs rounded bg-n-slate-3 dark:bg-n-slate-7 text-n-slate-11 font-bold"
          >
            {{ $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISABLED_BADGE') }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <template v-if="procedure.steps.length > 1">
            <NextButton
              icon="i-lucide-arrow-up-from-line"
              ghost
              sm
              :disabled="stepIndex === 0"
              :title="$t('ASSISTANT_MGMT.PROCEDURES.MOVE_UP')"
              @click="move(stepIndex, -1)"
            />
            <NextButton
              icon="i-lucide-arrow-down-from-line"
              ghost
              sm
              :disabled="stepIndex === procedure.steps.length - 1"
              :title="$t('ASSISTANT_MGMT.PROCEDURES.MOVE_DOWN')"
              @click="move(stepIndex, 1)"
            />
          </template>
          <Switch
            v-if="step.type === 'optional'"
            :model-value="!step.disabled"
            :title="
              step.disabled
                ? $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.ENABLE_OPTIONAL')
                : $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISABLE_OPTIONAL')
            "
            @click.stop="toggleDisabled(stepIndex)"
          />
        </div>
      </div>

      <div
        class="px-3 border rounded-lg min-h-[120px] relative cursor-default border-n-slate-5 dark:border-n-slate-7 bg-n-slate-2 dark:bg-n-slate-3"
        :class="{
          'opacity-40 grayscale pointer-events-none': step.disabled,
          'text-n-slate-11 dark:text-n-slate-10': !step.disabled,
        }"
      >
        <div
          class="tiptap pointer-events-none select-none"
          v-html="instructionHtml(step)"
        />
      </div>
    </div>
  </div>
</template>
